import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Skeleton,
  Snackbar, Alert, InputAdornment, Tooltip, Switch, FormControlLabel,
  Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import { Plus, Pencil, Trash2, X, Link2, ListTree, ArrowUpDown, Eye, GraduationCap } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetMenus,
  adminCreateMenu,
  adminUpdateMenu,
  adminDeleteMenu,
} from "../../api/Menus";

// NOTE: these must match the backend's exact enum spelling — COURSESMEGA
// has no underscore, per the Menu API Frontend Integration Guide.
const LINK_TYPES = ["LINK", "DROPDOWN", "COURSESMEGA"];
const VISIBILITIES = ["ALL", "STUDENT", "BUSINESS"];

const LINK_TYPE_LABELS = {
  LINK: "Link",
  DROPDOWN: "Dropdown (holds children)",
  COURSESMEGA: "All Courses mega menu",
};

const VISIBILITY_STYLES = {
  ALL:      { bg: "rgba(37,99,235,0.1)",  color: "#2563EB" },
  STUDENT:  { bg: "rgba(34,197,94,0.1)",  color: "#16A34A" },
  BUSINESS: { bg: "rgba(245,158,11,0.1)", color: "#B45309" },
};

// Normalizes either a raw array response or a DRF-paginated
// { count, next, previous, results: [...] } response into a plain array.
function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

const DEFAULT_VALUES = {
  label: "",
  linkType: "LINK",
  url: "",
  openNewTab: false,
  parent: "",
  order: 0,
  visibility: "ALL",
  isActive: true,
};

export default function MenuAdminPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: DEFAULT_VALUES });
  const watchLinkType = watch("linkType");

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const { data: menus = [], isLoading } = useQuery({
    queryKey: ["admin-menus"],
    queryFn: () => adminGetMenus().then((r) => toArray(r.data)),
  });

  // Only top-level DROPDOWN items can be a parent — the backend enforces a
  // max depth of 2, so a DROPDOWN that already has a parent (m.parent) is
  // not a valid parent option, and an item can't be its own parent.
  const parentOptions = menus.filter(
    (m) => m.linkType === "DROPDOWN" && !m.parent && m.id !== editing?.id
  );

  const createMutation = useMutation({
    mutationFn: adminCreateMenu,
    onSuccess: () => {
      qc.invalidateQueries(["admin-menus"]);
      notify("Menu item created");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create menu item", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateMenu(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-menus"]);
      notify("Menu item updated");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update menu item", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteMenu,
    onSuccess: () => {
      qc.invalidateQueries(["admin-menus"]);
      notify("Menu item deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      const status = err?.response?.status;
      const message =
        status === 409
          ? "This dropdown still has child items — reassign or delete them first."
          : err?.response?.data?.message || "Couldn't delete menu item";
      notify(message, "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (values) => {
    const payload = {
      ...values,
      parent: values.parent || null,
      order: Number(values.order) || 0,
      // A dropdown/mega-menu trigger has no destination of its own.
      url: values.linkType === "LINK" ? values.url?.trim() : null,
      // Only meaningful for LINK; clear it otherwise so the backend doesn't
      // reject the payload for a DROPDOWN/COURSESMEGA item.
      openNewTab: values.linkType === "LINK" ? !!values.openNewTab : false,
    };
    if (editing) updateMutation.mutate({ id: editing.id, data: payload });
    else createMutation.mutate(payload);
  };

  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    reset({
      label: m.label || "",
      linkType: m.linkType || "LINK",
      url: m.url || "",
      openNewTab: !!m.openNewTab,
      parent: m.parent || "",
      order: m.order ?? 0,
      visibility: m.visibility || "ALL",
      isActive: m.isActive ?? true,
    });
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    reset(DEFAULT_VALUES);
  };

  // Sort: top-level items first (by order), each followed by its children (by order).
  const topLevel = menus.filter((m) => !m.parent).sort((a, b) => a.order - b.order);
  const childrenOf = (id) => menus.filter((m) => m.parent === id).sort((a, b) => a.order - b.order);
  const orderedRows = topLevel.flatMap((m) => [
    { ...m, depth: 0 },
    ...childrenOf(m.id).map((c) => ({ ...c, depth: 1 })),
  ]);

  const findLabel = (id) => menus.find((m) => m.id === id)?.label || "—";

  return (
    <AdminShell title="Navbar Menus">
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 480 }}>
          Controls the public navbar. "Dropdown" items act as parents — create the dropdown
          first, then add child links under it via the Parent field.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)", flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" } }}
        >
          Add Menu Item
        </Button>
      </Box>

      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Label", "Type", "URL", "Visibility", "Order", "Status", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, letterSpacing: "0.03em", textTransform: "uppercase", color: "text.secondary", borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 180 : 90} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : orderedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <ListTree size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>No menu items yet</Typography>
                    <Typography color="text.secondary" fontSize={13.5}>Add your first navbar item to get started.</Typography>
                  </TableCell>
                </TableRow>
              ) : orderedRows.map((m) => (
                <TableRow key={m.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: m.depth * 3 }}>
                      {m.depth > 0 && <Box sx={{ width: 14, height: 1, bgcolor: "rgba(15,23,42,0.2)" }} />}
                      {m.linkType === "COURSESMEGA" ? <GraduationCap size={15} color="#2563EB" /> : <Link2 size={14} color="#94A3B8" />}
                      <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A" }}>{m.label}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{LINK_TYPE_LABELS[m.linkType] || m.linkType}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography sx={{ fontSize: 12.5, fontFamily: m.url ? "monospace" : "inherit", color: m.url ? "rgba(15,23,42,0.6)" : "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.url || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={m.visibility} size="small" sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: VISIBILITY_STYLES[m.visibility]?.bg, color: VISIBILITY_STYLES[m.visibility]?.color }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                      <ArrowUpDown size={12} />
                      <Typography sx={{ fontSize: 13 }}>{m.order}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={m.isActive ? "ACTIVE" : "INACTIVE"}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: m.isActive ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", color: m.isActive ? "#16A34A" : "#DC2626" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(m)} sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px", "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" } }}>
                          <Pencil size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setConfirmDelete(m)} sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px", "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" } }}>
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}>
        <Box sx={{ background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)", px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "white" }}>{editing ? "Edit Menu Item" : "New Menu Item"}</Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
              Controls how this item appears in the public navbar.
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeDialog} disabled={saving} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <X size={20} />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}>
            <Controller
              name="label"
              control={control}
              rules={{ required: "Label is required" }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Label" fullWidth disabled={saving} error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />

            <Controller
              name="linkType"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Type" fullWidth disabled={saving}>
                  {LINK_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{LINK_TYPE_LABELS[t]}</MenuItem>
                  ))}
                </TextField>
              )}
            />

            {watchLinkType === "LINK" && (
              <>
                <Controller
                  name="url"
                  control={control}
                  rules={{ required: "URL is required for a link" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="URL"
                      fullWidth
                      disabled={saving}
                      placeholder="/about-us  or  https://example.com"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || "Internal path (starts with /) or full external URL"}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Link2 size={15} color="#94A3B8" /></InputAdornment> }}
                    />
                  )}
                />
                <Controller
                  name="openNewTab"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={saving} />}
                      label="Open in a new tab"
                    />
                  )}
                />
              </>
            )}

            <Controller
              name="parent"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Parent (optional)" fullWidth disabled={saving} helperText="Leave blank for a top-level item">
                  <MenuItem value="">— Top level —</MenuItem>
                  {parentOptions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Controller
                name="order"
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Order" fullWidth disabled={saving} />
                )}
              />
              <Controller
                name="visibility"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Visible to" fullWidth disabled={saving}>
                    {VISIBILITIES.map((v) => (
                      <MenuItem key={v} value={v}>
                        <Chip label={v} size="small" sx={{ fontWeight: 600, fontSize: 12, height: 20, bgcolor: VISIBILITY_STYLES[v]?.bg, color: VISIBILITY_STYLES[v]?.color }} />
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Box>

            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={saving} />}
                  label="Active (visible on the site)"
                />
              )}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#2563EB", px: 2.5, minWidth: 100, "&:hover": { bgcolor: "#1D4ED8" } }}
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => !deleteMutation.isPending && setConfirmDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this menu item?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.label}" will be removed from the navbar immediately.
            {confirmDelete?.linkType === "DROPDOWN" && " Any children under it should be reassigned or deleted first — the API returns a 409 conflict otherwise."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleteMutation.isPending} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#EF4444", px: 2.5, minWidth: 100, "&:hover": { bgcolor: "#DC2626" } }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}